import { Button } from "@d12labs/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@d12labs/ui/card";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Textarea } from "@d12labs/ui/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Mic } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { speechCoachApi } from "../api";

// Sample PlanGenInput JSON — helps the user understand the expected shape.
const SAMPLE_INPUT = `{
  "speakerName": "Jane Smith",
  "speakerEmail": "jane@example.com",
  "totalAnalyzedMeetings": 5,
  "dateRange": {
    "earliest": "2026-05-01",
    "latest": "2026-05-28"
  },
  "averages": {
    "clarity": 6.5,
    "conciseness": 5.8,
    "confidence": 7.2,
    "engagement": 6.0,
    "structure": 5.5,
    "professionalism": 7.8
  },
  "perMeeting": [
    {
      "date": "2026-05-28",
      "meetingType": "team standup",
      "scores": {
        "clarity": 7.0,
        "conciseness": 6.0,
        "confidence": 7.5,
        "engagement": 6.5,
        "structure": 5.0,
        "professionalism": 8.0
      }
    }
  ],
  "notableQuotes": [
    {
      "dimensionKey": "conciseness",
      "sentiment": "negative",
      "timestampSec": 120,
      "text": "So basically what I was trying to say is, um, you know...",
      "meetingTitle": "Team Standup",
      "meetingDate": "2026-05-28"
    }
  ],
  "analytics": {
    "avgFillerPerMin": 3.2,
    "avgLongestMonologueSec": 145,
    "avgQuestionsPerMeeting": 2.1,
    "avgTalkToListen": 0.65
  },
  "derivedFromAnalysisIds": []
}`;

/** The def's Zod-inferred body — the pasted JSON is cast to it, validated server-side. */
type PlanGenInput = Parameters<typeof speechCoachApi.generatePlan>[0]["input"];

export function OnboardingPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [jsonInput, setJsonInput] = useState(SAMPLE_INPUT);
	const [inlineError, setInlineError] = useState<string | null>(null);

	const generate = useMutation({
		mutationFn: async () => {
			let parsed: unknown;
			try {
				parsed = JSON.parse(jsonInput);
			} catch (err) {
				const message = err instanceof Error ? err.message : "Invalid JSON";
				throw new Error(`Not valid JSON: ${message}`);
			}
			// User-pasted JSON: the cast only satisfies the typed caller — the def's
			// Zod schema is the real gate (invalid shapes still 400 server-side).
			return speechCoachApi.generatePlan({ input: parsed as PlanGenInput });
		},
		onSuccess: () => {
			setInlineError(null);
			toast.success("Plan generated");
			queryClient.invalidateQueries({ queryKey: ["speech-coach", "plan"] });
			queryClient.invalidateQueries({ queryKey: ["speech-coach", "drills"] });
			navigate({ to: "/speech-coach" });
		},
		onError: (err) => {
			const message = err instanceof Error ? err.message : "Failed to generate plan";
			setInlineError(message);
			toast.error(message);
		},
	});

	return (
		<>
			<PageHeader
				title="Onboarding"
				icon={Mic}
				subtitle="Generate your 2-week coaching plan from your meeting analyses."
			/>
			<PageBody className="p-6 space-y-6 mx-auto max-w-3xl w-full">
				<Card>
					<CardHeader>
						<CardTitle>Paste PlanGenInput JSON</CardTitle>
					</CardHeader>
					<CardContent className="flex flex-col gap-4">
						<Textarea
							className="font-mono text-sm min-h-[400px]"
							value={jsonInput}
							onChange={(e) => {
								setJsonInput(e.target.value);
								setInlineError(null);
							}}
							placeholder="Paste your PlanGenInput JSON here..."
						/>
						{inlineError ? <div className="text-sm text-destructive">{inlineError}</div> : null}
						<div className="flex items-center gap-4">
							<Button onClick={() => generate.mutate()} disabled={generate.isPending}>
								{generate.isPending ? "Generating…" : "Generate plan"}
							</Button>
							<p className="text-body-sm text-ink-subtle">
								Paste a PlanGenInput JSON. B6.1 will fetch this automatically from your meeting-perf
								data.
							</p>
						</div>
					</CardContent>
				</Card>
			</PageBody>
		</>
	);
}
